import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Printer, Search, FileText, Truck, Users, CheckCircle2, TrendingDown, AlertCircle, Clock } from "lucide-react";
import { KpiCard, StatusBadge } from "@/components/erp/primitives";
import TableExportButton from "@/components/TableExportButton";

const fmtINR = (n) =>
  `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const StatusPill = ({ status, days = 0 }) => {
  const k = String(status || "").toLowerCase();
  if (k === "paid") return <StatusBadge status="paid" label="Paid" />;
  if (days >= 60) return <StatusBadge status="rejected" label="Overdue" />;
  if (k === "partially paid") return <StatusBadge status="partial" label="Partial" />;
  return <StatusBadge status="pending" label="Pending" />;
};

// Shared skeleton used by both Supplier & Customer ledger pages.
function LedgerScaffold({
  kind,           // "supplier" | "customer"
  name,
  data,
  loading,
  filters,
  onFiltersChange,
  columns,        // array of { key, label, align?, cls? }
  rows,           // filtered rows
  exportColumns,
  testidPrefix,
  summaryCards,   // JSX
}) {
  const nav = useNavigate();
  const icon = kind === "supplier" ? Truck : Users;
  const Icon = icon;
  return (
    <div className="space-y-6" data-testid={`${kind}-ledger-page`}>
      {/* HEADER */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500">Accounts &middot; {kind === "supplier" ? "Supplier" : "Customer"} Ledger</div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900 inline-flex items-center gap-2">
            <Icon size={22} /> {name}
          </h1>
          <p className="text-sm text-slate-500 mt-1">Complete transaction history · {rows.length} matching {kind === "supplier" ? "bills" : "invoices"}.</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/accounts-dashboard" className="inline-flex items-center gap-1.5 text-sm text-slate-700 hover:text-[#1D2951] px-3 py-2 border border-slate-300 rounded-md">
            <ArrowLeft size={14} /> Back to Accounts Dashboard
          </Link>
          <button
            onClick={() => window.print()}
            data-testid="ledger-print"
            className="inline-flex items-center gap-1.5 text-sm text-slate-700 hover:text-[#1D2951] px-3 py-2 border border-slate-300 rounded-md"
          >
            <Printer size={14} /> Print
          </button>
        </div>
      </div>

      {/* SUMMARY */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {summaryCards}
      </div>

      {/* FILTERS */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-wrap items-center gap-3 shadow-sm">
        <div className="inline-flex items-center gap-2 bg-slate-100 rounded-md px-3 py-1.5 text-xs">
          <Search size={12} className="text-slate-500" />
          <input value={filters.q} onChange={(e) => onFiltersChange({ ...filters, q: e.target.value })}
            placeholder="Search…" data-testid={`${testidPrefix}-search`}
            className="bg-transparent outline-none text-sm min-w-[200px]" />
        </div>
        <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-md px-3 py-1.5">
          <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">From</span>
          <input type="date" value={filters.from} onChange={(e) => onFiltersChange({ ...filters, from: e.target.value })}
            data-testid={`${testidPrefix}-from`}
            className="bg-transparent text-xs font-mono text-slate-900 focus:outline-none" />
          <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold border-l border-slate-200 pl-2">To</span>
          <input type="date" value={filters.to} onChange={(e) => onFiltersChange({ ...filters, to: e.target.value })}
            data-testid={`${testidPrefix}-to`}
            className="bg-transparent text-xs font-mono text-slate-900 focus:outline-none" />
        </div>
        <select value={filters.status} onChange={(e) => onFiltersChange({ ...filters, status: e.target.value })}
          data-testid={`${testidPrefix}-status`}
          className="border border-slate-300 rounded-md px-3 py-1.5 text-sm bg-white">
          <option value="all">All Status</option>
          <option value="pending">Pending</option>
          <option value="partial">Partially Paid</option>
          <option value="paid">Paid</option>
        </select>
        <div className="ml-auto">
          <TableExportButton
            testIdPrefix={`${testidPrefix}-export`}
            filename={`${kind}_ledger_${name.replace(/\s+/g, "_")}`}
            rows={rows}
            columns={exportColumns}
          />
        </div>
      </div>

      {/* LEDGER TABLE */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          {loading ? (
            <div className="text-center py-20 text-slate-400 text-sm">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="text-center py-20 text-slate-400 text-sm">No transactions match the current filters.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
                  {columns.map((c) => (
                    <th key={c.key} className={`px-4 py-3 ${c.align === "right" ? "text-right" : "text-left"}`}>{c.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody data-testid={`${testidPrefix}-body`}>
                {rows.map((r) => (
                  <tr key={r.id} className={`border-t border-slate-100 hover:bg-slate-50 ${r._overdue ? "bg-rose-50/40" : ""}`}>
                    {columns.map((c) => (
                      <td key={c.key} className={`px-4 py-3 ${c.align === "right" ? "text-right" : ""} ${c.cls || ""}`}>
                        {typeof c.render === "function" ? c.render(r) : (r[c.key] ?? "—")}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// SUPPLIER LEDGER
// -----------------------------------------------------------------------------
export function SupplierLedger() {
  const { name } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ q: "", from: "", to: "", status: "all" });

  useEffect(() => {
    setLoading(true);
    api.get(`/accounts-dashboard/supplier-ledger/${encodeURIComponent(name)}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load ledger"))
      .finally(() => setLoading(false));
  }, [name]);

  const rows = useMemo(() => {
    if (!data) return [];
    const q = filters.q.trim().toLowerCase();
    return (data.bills || [])
      .filter((b) => (!filters.from || (b.bill_date && b.bill_date >= filters.from))
                  && (!filters.to || (b.bill_date && b.bill_date <= filters.to)))
      .filter((b) => filters.status === "all"
                  || (filters.status === "paid" && (b.status || "").toLowerCase() === "paid")
                  || (filters.status === "partial" && (b.status || "").toLowerCase() === "partially paid")
                  || (filters.status === "pending" && (b.status || "").toLowerCase() === "pending"))
      .filter((b) => !q || `${b.supplier_bill_number} ${b.grn_number}`.toLowerCase().includes(q))
      .map((b) => ({ ...b, _overdue: b.balance_amount > 0.01 && b.days_since_bill >= 60 }));
  }, [data, filters]);

  const s = data?.summary || {};

  return (
    <LedgerScaffold
      kind="supplier"
      name={name}
      data={data}
      loading={loading}
      filters={filters}
      onFiltersChange={setFilters}
      testidPrefix="supplier-ledger"
      columns={[
        { key: "supplier_bill_number", label: "Bill No", cls: "font-mono text-xs" },
        { key: "grn_number", label: "GRN No", cls: "font-mono text-xs" },
        { key: "grn_date", label: "GRN Date", cls: "font-mono text-xs" },
        { key: "bill_date", label: "Bill Date", cls: "font-mono text-xs" },
        { key: "bill_amount", label: "Bill Amt", align: "right", render: (r) => <span className="font-mono">{fmtINR(r.bill_amount)}</span> },
        { key: "paid_amount", label: "Paid", align: "right", render: (r) => <span className="font-mono text-emerald-700">{fmtINR(r.paid_amount)}</span> },
        { key: "balance_amount", label: "Balance", align: "right", render: (r) => <span className="font-mono text-amber-700 font-semibold">{fmtINR(r.balance_amount)}</span> },
        { key: "payment_vouchers", label: "Voucher No(s)", cls: "font-mono text-xs", render: (r) => (r.payment_vouchers || []).join(", ") || "—" },
        { key: "payment_dates", label: "Payment Date(s)", cls: "font-mono text-xs", render: (r) => (r.payment_dates || []).join(", ") || "—" },
        { key: "days_since_bill", label: "Days", align: "right", render: (r) => <span className={`font-mono ${r._overdue ? "text-rose-700 font-semibold" : "text-slate-600"}`}>{r.days_since_bill}</span> },
        { key: "status", label: "Status", render: (r) => <StatusPill status={r.status} days={r.days_since_bill} /> },
      ]}
      rows={rows}
      exportColumns={[
        { key: "supplier_bill_number", label: "Bill No" },
        { key: "grn_number", label: "GRN No" },
        { key: "grn_date", label: "GRN Date" },
        { key: "bill_date", label: "Bill Date" },
        { key: "bill_amount", label: "Bill Amount" },
        { key: "paid_amount", label: "Paid" },
        { key: "balance_amount", label: "Balance" },
        { key: "days_since_bill", label: "Days" },
        { key: "status", label: "Status" },
      ]}
      summaryCards={
        <>
          <KpiCard testid="sup-sum-purchase" title="Total Purchase" value={fmtINR(s.total_purchase_value)} icon={FileText} variant="blue" />
          <KpiCard testid="sup-sum-paid" title="Total Paid" value={fmtINR(s.total_amount_paid)} icon={CheckCircle2} variant="emerald" />
          <KpiCard testid="sup-sum-outstanding" title="Outstanding" value={fmtINR(s.total_outstanding)} icon={TrendingDown} variant="amber" />
          <KpiCard testid="sup-sum-bills" title="Bills" value={s.number_of_bills ?? 0} icon={FileText} variant="slate" />
          <KpiCard testid="sup-sum-oldest" title="Oldest Due (Days)" value={s.oldest_outstanding_days ?? 0} icon={AlertCircle} variant={(s.oldest_outstanding_days || 0) >= 60 ? "rose" : "sky"} />
          <KpiCard testid="sup-sum-avg" title="Avg Credit (Days)" value={s.average_credit_days ?? 0} icon={Clock} variant="sky" />
        </>
      }
    />
  );
}

// -----------------------------------------------------------------------------
// CUSTOMER LEDGER
// -----------------------------------------------------------------------------
export function CustomerLedger() {
  const { name } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ q: "", from: "", to: "", status: "all" });

  useEffect(() => {
    setLoading(true);
    api.get(`/accounts-dashboard/customer-ledger/${encodeURIComponent(name)}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load ledger"))
      .finally(() => setLoading(false));
  }, [name]);

  const rows = useMemo(() => {
    if (!data) return [];
    const q = filters.q.trim().toLowerCase();
    return (data.invoices || [])
      .filter((i) => (!filters.from || (i.invoice_date && i.invoice_date >= filters.from))
                  && (!filters.to || (i.invoice_date && i.invoice_date <= filters.to)))
      .filter((i) => filters.status === "all"
                  || (filters.status === "paid" && (i.status || "").toLowerCase() === "paid")
                  || (filters.status === "partial" && (i.status || "").toLowerCase() === "partially paid")
                  || (filters.status === "pending" && (i.status || "").toLowerCase() === "pending"))
      .filter((i) => !q || `${i.invoice_number}`.toLowerCase().includes(q))
      .map((i) => ({ ...i, _overdue: i.balance_amount > 0.01 && i.days_since_invoice >= 60 }));
  }, [data, filters]);

  const s = data?.summary || {};

  return (
    <LedgerScaffold
      kind="customer"
      name={name}
      data={data}
      loading={loading}
      filters={filters}
      onFiltersChange={setFilters}
      testidPrefix="customer-ledger"
      columns={[
        { key: "invoice_number", label: "Invoice No", cls: "font-mono text-xs" },
        { key: "invoice_date", label: "Invoice Date", cls: "font-mono text-xs" },
        { key: "invoice_value", label: "Invoice Value", align: "right", render: (r) => <span className="font-mono">{fmtINR(r.invoice_value)}</span> },
        { key: "amount_received", label: "Received", align: "right", render: (r) => <span className="font-mono text-emerald-700">{fmtINR(r.amount_received)}</span> },
        { key: "balance_amount", label: "Balance", align: "right", render: (r) => <span className="font-mono text-amber-700 font-semibold">{fmtINR(r.balance_amount)}</span> },
        { key: "receipt_numbers", label: "Receipt No(s)", cls: "font-mono text-xs", render: (r) => (r.receipt_numbers || []).join(", ") || "—" },
        { key: "receipt_dates", label: "Receipt Date(s)", cls: "font-mono text-xs", render: (r) => (r.receipt_dates || []).join(", ") || "—" },
        { key: "days_since_invoice", label: "Days", align: "right", render: (r) => <span className={`font-mono ${r._overdue ? "text-rose-700 font-semibold" : "text-slate-600"}`}>{r.days_since_invoice}</span> },
        { key: "status", label: "Status", render: (r) => <StatusPill status={r.status} days={r.days_since_invoice} /> },
      ]}
      rows={rows}
      exportColumns={[
        { key: "invoice_number", label: "Invoice No" },
        { key: "invoice_date", label: "Invoice Date" },
        { key: "invoice_value", label: "Invoice Value" },
        { key: "amount_received", label: "Received" },
        { key: "balance_amount", label: "Balance" },
        { key: "days_since_invoice", label: "Days" },
        { key: "status", label: "Status" },
      ]}
      summaryCards={
        <>
          <KpiCard testid="cust-sum-sales" title="Total Sales" value={fmtINR(s.total_sales)} icon={FileText} variant="blue" />
          <KpiCard testid="cust-sum-received" title="Received" value={fmtINR(s.total_amount_received)} icon={CheckCircle2} variant="emerald" />
          <KpiCard testid="cust-sum-outstanding" title="Outstanding" value={fmtINR(s.total_outstanding)} icon={TrendingDown} variant="amber" />
          <KpiCard testid="cust-sum-invoices" title="Invoices" value={s.number_of_invoices ?? 0} icon={FileText} variant="slate" />
          <KpiCard testid="cust-sum-oldest" title="Oldest Due (Days)" value={s.oldest_outstanding_days ?? 0} icon={AlertCircle} variant={(s.oldest_outstanding_days || 0) >= 60 ? "rose" : "sky"} />
          <KpiCard testid="cust-sum-avg" title="Avg Collection (Days)" value={s.average_collection_days ?? 0} icon={Clock} variant="sky" />
        </>
      }
    />
  );
}
